/* eslint-disable no-unused-vars */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllBlogs } from '../../redux/features/blogsSlice';
import { fetchAds } from '../../redux/features/adsSlice';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement);

const DashboardPage = () => {
    const dispatch = useDispatch();
    const { items: blogs, status: blogsStatus } = useSelector(state => state.blogs);
    const { items: ads, status: adsStatus } = useSelector(state => state.ads);
    const { user } = useSelector(state => state.auth);
    
    useEffect(() => {
        dispatch(fetchAllBlogs());
        dispatch(fetchAds());
    }, [dispatch]);

    const publishedBlogs = blogs.filter(blog => blog.published);
    const draftBlogs = blogs.filter(blog => !blog.published);
    
    const activeAds = ads.filter(ad => ad.active);
    const inactiveAds = ads.filter(ad => !ad.active);

    const pieData = {
        labels: ['Published', 'Drafts'],
        datasets: [{
            data: [publishedBlogs.length, draftBlogs.length],
            backgroundColor: ['#4F46E5', '#E5E7EB'],
        }]
    };
    
    const adPieData = {
        labels: ['Active Ads', 'Inactive Ads'],
        datasets: [{
            data: [activeAds.length, inactiveAds.length],
            backgroundColor: ['#10B981', '#F59E0B'],
        }]
    };

    const recentBlogs = [...blogs]
        .sort((a, b) => {
            const dateA = new Date(a.createdAt.seconds * 1000 + a.createdAt.nanoseconds / 1000000);
            const dateB = new Date(b.createdAt.seconds * 1000 + b.createdAt.nanoseconds / 1000000);
            return dateB - dateA;
        })
        .slice(0, 5);
        
    const recentAds = [...ads]
        .sort((a, b) => {
            if (!a.createdAt || !b.createdAt) return 0;
            const dateA = new Date(a.createdAt.seconds * 1000 + a.createdAt.nanoseconds / 1000000);
            const dateB = new Date(b.createdAt.seconds * 1000 + b.createdAt.nanoseconds / 1000000);
            return dateB - dateA;
        })
        .slice(0, 3);

    return (
        <div>
            <div className="md:flex md:items-center md:justify-between">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
                        Welcome back, {user.email}
                    </h2>
                </div>
                <div className="mt-4 flex md:ml-4 md:mt-0">
                    <Link
                        to="/admin/ads"
                        className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-3"
                    >
                        Manage Ads
                    </Link>
                    <Link
                        to="/admin/blogs/new"
                        className="ml-3 inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                    >
                        Create New Blog
                    </Link>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Total Blogs
                                    </dt>
                                    <dd className="text-3xl font-semibold text-gray-900">
                                        {blogs.length}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Published Blogs
                                    </dt>
                                    <dd className="text-3xl font-semibold text-gray-900">
                                        {publishedBlogs.length}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">
                                        Total Ads
                                    </dt>
                                    <dd className="text-3xl font-semibold text-gray-900">
                                        {ads.length}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Blog Status</h3>
                        <div className="h-64">
                            <Pie data={pieData} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Ad Status</h3>
                        <div className="h-64">
                            <Pie data={adPieData} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Recent Blogs</h3>
                        <div className="mt-4">
                            <ul className="divide-y divide-gray-200">
                                {recentBlogs.map(blog => (
                                    <li key={blog.id} className="py-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {blog.title}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {new Date(blog.createdAt.seconds * 1000 + blog.createdAt.nanoseconds / 1000000).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <div>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${blog.published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {blog.published ? 'Published' : 'Draft'}
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Recent Ads</h3>
                        <div className="mt-4">
                            <ul className="divide-y divide-gray-200">
                                {recentAds.map(ad => (
                                    <li key={ad.id} className="py-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {ad.title}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {ad.type} - {ad.position}
                                                </p>
                                            </div>
                                            <div>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ad.active ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                    {ad.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;

